import type { RaygunV2 } from 'raygun4js';
import {
  DraftConfiguration,
  DraftConfigurationColor,
  DraftConfigurationMask,
  DraftConfigurationSpecialText,
  DraftConfigurationText,
  DraftState,
} from '@/types';
import { isObject } from '@/utils/isObject';
import {
  isDraftDesignConfiguration,
  isDraftSpecialTextConfiguration,
} from '@/model/configuration/isDraftConfigurationType';

declare global {
  interface Window {
    rg4js: RaygunV2;
  }
}

export const setOnBeforeSend = (fn: (payload: any) => any) => {
  if (window.rg4js) {
    window.rg4js('onBeforeSend', (payload: any) => {
      if (payload && payload.Details && payload.Details.User) {
        try {
          localStorage.setItem('RayGunUser', JSON.stringify(payload.Details.User));
        } catch (e) {
          console.warn('Could not set raygun data to local storage');
        }
      }
      return fn(payload);
    });
  }
};

export const trackError = (err: any, customData?: any) => {
  if (err && window.rg4js) {
    const error = err instanceof Error ? err : new Error(err);
    window.rg4js('send', { error, customData });
  }
};

export type RaygunBreadCrumbMetaData = { action: string; draft?: RaygunDraft };

export const addBreadcrumb = (message: string, metadata: RaygunBreadCrumbMetaData) => {
  if (window.rg4js) {
    window.rg4js('recordBreadcrumb', message, metadata);
  }
};

const mapPrintColorToRaygunPrintColor = ({ id, name, fill }: DraftConfigurationColor) => ({
  id,
  name,
  fill,
});

const mapMaskToRaygunMask = ({ id, name, contentType, scale, offset }: DraftConfigurationMask) => ({
  id,
  name,
  contentType,
  scale,
  offset,
});

const mapSpecialTextToRaygunText = ({
  content,
  style: { font, textAlign },
}: DraftConfigurationSpecialText) => {
  let fontData = {};
  if (font) {
    const { type, identifier, title } = font;
    fontData = {
      type,
      identifier,
      title,
    };
  }
  return {
    content,
    style: {
      font: fontData,
      textAlign,
    },
  };
};

const mapTextToRaygunText = ({
  content,
  style: { font, textAlign, fontSize, angle, x, y },
}: DraftConfigurationText) => {
  let fontData = {};
  if (font) {
    const { type, identifier, name, weight, style } = font;
    fontData = {
      type,
      identifier,
      name,
      weight,
      style,
    };
  }

  return {
    content,
    style: {
      font: fontData,
      textAlign,
      fontSize,
      angle,
      x,
      y,
    },
  };
};

const mapConfigurationToRaygunConfiguration = (config: DraftConfiguration) => {
  return {
    cid: config.cid,
    type: config.type,
    printTypeId: config.printType.id,
    printAreaId: config.printArea.id,
    printColors: config.printColors.length
      ? config.printColors.map(mapPrintColorToRaygunPrintColor)
      : undefined,
    scale: config.scale,
    size: config.size,
    offset: config.offset,
    rotation: config.rotation,
    ...(isDraftDesignConfiguration(config)
      ? {
          design: config.design.id,
          filter: config.filter && { ...config.filter },
          mask: config.mask && mapMaskToRaygunMask(config.mask),
          processedSize: config.processedSize,
          processedImage: config.processedImage,
        }
      : {
          text: isDraftSpecialTextConfiguration(config)
            ? mapSpecialTextToRaygunText(config.text)
            : mapTextToRaygunText(config.text),
        }),
  };
};

export const mapDraftToRaygunDraft = ({
  productType,
  appearance,
  view,
  selectedSizes,
  basketItemId,
  validation,
  configurations,
}: DraftState) => {
  return {
    productTypeId: productType.id,
    appearanceId: appearance.id,
    viewId: view.id,
    selectedSizes,
    basketItemId,
    validation: validation.valid || validation.errors,
    configurations: Object.values(configurations).map(mapConfigurationToRaygunConfiguration),
  };
};

export type RaygunDraft = ReturnType<typeof mapDraftToRaygunDraft>;

export const processErrorForCustomData = (error: unknown) => {
  const errorData: Record<string, any> = {};
  if (isObject(error)) {
    // save all available properties from the error into an object so they get tracked
    Object.getOwnPropertyNames(error).forEach((prop) => {
      errorData[prop] = error[prop];
    });
  } else {
    errorData.error = error;
  }

  return errorData;
};
